'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  REGISTRATION_CONFIRMATION, CONFIRM_LINK_EXISTING, COMPLETE_REGISTRATION,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { mapRegistrationError } from './registration-errors';

interface ConfirmationContextData {
  registrationConfirmation: {
    mode: 'LINK_EXISTING' | 'NEW_ACCOUNT';
    email: string;
    customerNumber: string;
    firstName: string | null;
    lastName: string | null;
    company: string | null;
    existingCustomerNumberWillBeReplaced: boolean;
  };
}

export function RegisterConfirm() {
  const token = useSearchParams().get('token') ?? '';
  const { data, loading, error } = useQuery<ConfirmationContextData>(REGISTRATION_CONFIRMATION, {
    variables: { token }, skip: !token,
  });
  const [done, setDone] = useState(false);

  if (!token) return <p className="text-center">Ungültiger Link.</p>;
  if (loading) return <p className="text-center">Lädt…</p>;
  if (error || !data) return <p className="text-center">Dieser Bestätigungslink ist ungültig oder abgelaufen.</p>;
  if (done) {
    return (
      <div className="text-center">
        <p className="mb-4">Ihr Zugang wurde <strong>erfolgreich</strong> eingerichtet.</p>
        <Link href="/login" className="font-medium text-[color:var(--color-primary)] underline">Zum Login</Link>
      </div>
    );
  }

  const ctx = data.registrationConfirmation;
  return ctx.mode === 'LINK_EXISTING'
    ? <LinkExisting token={token} ctx={ctx} onDone={() => setDone(true)} />
    : <NewAccount token={token} ctx={ctx} onDone={() => setDone(true)} />;
}

function LinkExisting({ token, ctx, onDone }: { token: string; ctx: any; onDone: () => void }) {
  const [link, { loading }] = useMutation(CONFIRM_LINK_EXISTING);
  const [err, setErr] = useState('');
  const submit = async () => {
    setErr('');
    try {
      await link({ variables: { token } });
      onDone();
    } catch (e) {
      setErr(mapRegistrationError((e as { message?: string }).message));
    }
  };
  return (
    <div className="space-y-4 text-left">
      <h2 className="text-xl font-semibold">Mit bestehendem Account verknüpfen</h2>
      <p>Für <strong>{ctx.email}</strong> existiert bereits ein Konto. Möchten Sie die
         Kundennummer <strong>{ctx.customerNumber}</strong> damit verknüpfen?</p>
      {ctx.existingCustomerNumberWillBeReplaced && (
        <p className="rounded bg-amber-50 p-3 text-sm text-amber-800">
          Hinweis: Eine bereits hinterlegte Kundennummer wird durch {ctx.customerNumber} <strong>ersetzt</strong>.
        </p>
      )}
      {err && <p className="rounded bg-red-50 p-3 text-sm text-red-700">{err}</p>}
      <button
        type="button" disabled={loading}
        onClick={submit}
        className="w-full rounded-lg bg-[color:var(--color-primary)] px-4 py-3 font-medium text-white disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-500"
      >
        Bestätigen
      </button>
    </div>
  );
}

function NewAccount({ token, ctx, onDone }: { token: string; ctx: any; onDone: () => void }) {
  const [firstName, setFirstName] = useState(ctx.firstName ?? '');
  const [lastName, setLastName] = useState(ctx.lastName ?? '');
  const [company, setCompany] = useState(ctx.company ?? '');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [err, setErr] = useState('');
  const [complete, { loading }] = useMutation(COMPLETE_REGISTRATION);

  const submit = async () => {
    setErr('');
    if (password.length < 8) { setErr('Passwort muss mindestens 8 Zeichen haben.'); return; }
    if (password !== password2) { setErr('Die Passwörter stimmen nicht überein.'); return; }
    try {
      await complete({ variables: {
        token, firstName: firstName.trim(), lastName: lastName.trim(),
        company: company.trim() || null, password,
      } });
      onDone();
    } catch (e) { setErr(mapRegistrationError((e as { message?: string }).message)); }
  };

  const field = 'w-full rounded-lg border border-zinc-300 px-3 py-2';
  return (
    <div className="space-y-3 text-left">
      <h2 className="mb-2 text-xl font-semibold">Registrierung abschließen</h2>
      {err && <p className="rounded bg-red-50 p-3 text-sm text-red-700">{err}</p>}
      <label className="block text-sm font-medium">E-Mail
        <input className={`${field} bg-zinc-100`} value={ctx.email} disabled aria-label="E-Mail" />
      </label>
      <label className="block text-sm font-medium">Kundennummer
        <input className={`${field} bg-zinc-100`} value={ctx.customerNumber} disabled aria-label="Kundennummer" />
      </label>
      <label className="block text-sm font-medium">Vorname
        <input className={field} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      </label>
      <label className="block text-sm font-medium">Nachname
        <input className={field} value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </label>
      <label className="block text-sm font-medium">Firma
        <input className={field} value={company} onChange={(e) => setCompany(e.target.value)} />
      </label>
      <label className="block text-sm font-medium">Passwort
        <input type="password" className={field} value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Passwort" />
      </label>
      <label className="block text-sm font-medium">Passwort wiederholen
        <input type="password" className={field} value={password2} onChange={(e) => setPassword2(e.target.value)} aria-label="Passwort wiederholen" />
      </label>
      <button
        type="button" disabled={loading} onClick={submit}
        className="w-full rounded-lg bg-[color:var(--color-primary)] px-4 py-3 font-medium text-white disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-500"
      >
        Registrieren
      </button>
    </div>
  );
}
